import React, {
  useEffect,
  useMemo,
} from "react";

import { ReadOnlyWrapper } from "../ReadOnlyWrapper";

import { useDatesHandler } from "./useDatesHandler";
import type { MatchDate } from "./types";
import { DatePicker } from "../../UI/DatePicker";
import { dayjs } from "../../services/dayjs";
import { Radio } from "../../UI/Radio";
import { Grid } from "../../UI/Grid";
import { Button } from "../../UI/Button";
import { Typography } from "../../UI/Typography";

import { ClosedDateChangeNotificationModal } from "../ClosedDateChangeNotificationModal";
import useAuth from "../../routes/login/AuthContext";

const getDateValue = (date?: dayjs.Dayjs | null) => date?.format("YYYY-MM-DD") || undefined;
const MATCHING_WINDOW_CONTENT ="Users can view potential roommates and send or confirm roommate requests between dates.";
const YEAR_ROUND_CONTENT = "This Matching Round is set to be open year round.  This means residents can register and match at any time with no end date.  To change this setting, please contact the RoomSync team at clients@roomsync.com.";
const REGISTRATION_WINDOW_CONTENT = "Users can optionally register and create their profiles during the Registration Window. They can still only send or confirm roommate requests during the Matching Window.";
const YEAR_ROUND_CONTENT_SUPER_ADMIN = "This Matching Round is set to be open year round.  This means residents can register and match at any time with no end date.";
export const MatchDatesAccordionContent: React.FC<MatchDate> = (props) => {
  const { isSuperAdminRead, isSuperAdmin } = useAuth();
  const {
    isPrevEndDateSelected,
    matchingStartDate,
    matchingEndDate,
    activationStartDate,
    activationEndDate,
    isEndLoading,
    onMatchingEndDateChange,
    onMatchingStartDateChange,
    onActivationStartDateChange,
    onActivationEndDateChange,
    onDeselectActivationDates,
    handleEndDateNotifyRequest,
    handleModalNotificationModalClose,
    handleDatesClear,
    areActivationDateEnabled,
    setActivationDateEnabled
  } = useDatesHandler(props);

  const areStartEndDatesSame = () => {
    if(!!matchingStartDate && !!activationStartDate && !!matchingEndDate && !!activationEndDate) {
      return matchingStartDate.isSame(activationStartDate, 'day') && matchingEndDate.isSame(activationEndDate, 'day');
    }
    return false;
  };

  useEffect(() => {
    if(areStartEndDatesSame()) {
      setActivationDateEnabled(false);
    } else {
      setActivationDateEnabled(true);
    }
  }, []);

  // useEffect(() => {
  //   onActivationStartDateChange(matchingStartDate);
  //   onActivationEndDateChange(matchingEndDate);
  // }, [areActivationDateEnabled]);

  const handleDeelectActivationDates = () => {
    setActivationDateEnabled(false);
    onDeselectActivationDates(matchingStartDate, matchingEndDate);
  };

  const isRowDisabled = useMemo(() => props.matchingRoundStatus === 'ARCHIVED' || isEndLoading, [isEndLoading]);
  const isNotYearRound = useMemo(() => !!matchingStartDate && !!matchingEndDate && !!activationStartDate && !!activationEndDate, [matchingStartDate, matchingEndDate, activationStartDate, activationEndDate]);

  return (
    <Grid container pb={3}>
      <Grid container xs={6}>
        <Grid container xs={9}>
          <Grid item xs={12}>
          <Typography variant="h6">
            Matching Window
          </Typography>
          </Grid>
          <Typography variant="body2">
            {isNotYearRound && MATCHING_WINDOW_CONTENT}
            {!isNotYearRound && !isSuperAdmin() && YEAR_ROUND_CONTENT}
            {!isNotYearRound && isSuperAdmin() && YEAR_ROUND_CONTENT_SUPER_ADMIN}
          </Typography>
        </Grid>
        {
          (isNotYearRound || (isSuperAdmin() && !isNotYearRound)) && <Grid container xs={12} gap={3} mt={1}>
          <Grid item xs={5}>
            <ReadOnlyWrapper>
              <DatePicker
                key={String(matchingStartDate)  + props.l2Id}
                label="Starts"
                onChange={onMatchingStartDateChange}
                value={getDateValue(matchingStartDate)}
                maxDate={getDateValue(matchingEndDate)}
                minDate={areActivationDateEnabled ? getDateValue(activationStartDate) : undefined}
                isDisabled={isSuperAdminRead() || isRowDisabled}
                innerProps={{ className: "custom-date-picker" }}
                customWidth="100%"
                isLoading={isEndLoading}
              />
            </ReadOnlyWrapper>
          </Grid>
          <Grid item xs={5}>
            <ReadOnlyWrapper>
              <DatePicker
                key={String(matchingEndDate) + props.l2Id}
                label="Ends"
                onChange={onMatchingEndDateChange}
                value={getDateValue(matchingEndDate)}
                minDate={getDateValue(matchingStartDate)}
                maxDate={areActivationDateEnabled? getDateValue(activationEndDate) : undefined}
                isDisabled={isSuperAdminRead() || isRowDisabled}
                innerProps={{ className: "custom-date-picker" }}
                customWidth="100%"
                isLoading={isEndLoading}
              />
            </ReadOnlyWrapper>
          </Grid>
        </Grid>
        }
      </Grid>
      {(isNotYearRound || (isSuperAdmin() && !isNotYearRound)) && 
      <Grid container xs={6}>
        <Grid container xs={9}>
          <Typography variant="h6">
            Registration Window
          </Typography>
          <Typography variant="body2">
            {REGISTRATION_WINDOW_CONTENT}
          </Typography>
          <Radio
            label="Allow additional access time"
            isDisabled={isSuperAdminRead() || props.matchingRoundStatus === "ARCHIVED"}
            isChecked={areActivationDateEnabled}
            onChange={() => setActivationDateEnabled(true)}
          />
          <Radio
            label="No additional access time, access only available during matching."
            isDisabled={isSuperAdminRead() || props.matchingRoundStatus === "ARCHIVED"}
            isChecked={!areActivationDateEnabled}
            onChange={handleDeelectActivationDates}
          />
        </Grid>
        {areActivationDateEnabled && 
        <Grid container xs={12} gap={3} mt={1}>
          <Grid item xs={5}>
            <ReadOnlyWrapper>
              <DatePicker
                  key={String(activationStartDate) + props.l2Id}
                  label="Starts"
                  onChange={onActivationStartDateChange}
                  value={getDateValue(activationStartDate)}
                  maxDate={getDateValue(matchingStartDate)}
                  isDisabled={isSuperAdminRead() || props.matchingRoundStatus === "ARCHIVED" || isEndLoading}
                  innerProps={{ className: "custom-date-picker" }}
                  customWidth="100%"
                  isLoading={isEndLoading}
              />
            </ReadOnlyWrapper>
          </Grid>
          <Grid item xs={5}>
            <ReadOnlyWrapper>
                <DatePicker
                    key={String(activationEndDate) + props.l2Id}
                    label="Ends"
                    onChange={onActivationEndDateChange}
                    value={getDateValue(activationEndDate)}
                    minDate={getDateValue(matchingEndDate)}
                    isDisabled={isSuperAdminRead() || props.matchingRoundStatus === "ARCHIVED" || isEndLoading}
                    innerProps={{ className: "custom-date-picker" }}
                    customWidth="100%"
                    isLoading={isEndLoading}
                />
            </ReadOnlyWrapper>
          </Grid>
        </Grid>
        }
      </Grid>}
      {isSuperAdmin() && <Grid container xs={6}>
        <Button appearance="danger" onClick={handleDatesClear}>
          Make Year Round
        </Button>
      </Grid>}
      {matchingEndDate && (
        <ClosedDateChangeNotificationModal
          date={matchingEndDate}
          l2Name={props.l2Name}
          isLoading={isEndLoading}
          usersInNetwork={props.usersInNetwork}
          handleRequest={handleEndDateNotifyRequest}
          onClose={handleModalNotificationModalClose}
          isPrevEndDateSelected={isPrevEndDateSelected}
        />
      )}
    </Grid>
  );
};
